/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import {
  MutationKey,
  useMutation,
  UseMutationResult,
} from "@tanstack/react-query";
import { GramCore } from "../core.js";
import { dashboardsRemoveWidget } from "../funcs/dashboardsRemoveWidget.js";
import { combineSignals } from "../lib/primitives.js";
import { RequestOptions } from "../lib/sdks.js";
import { Dashboard } from "../models/components/dashboard.js";
import { GramError } from "../models/errors/gramerror.js";
import {
  ConnectionError,
  InvalidRequestError,
  RequestAbortedError,
  RequestTimeoutError,
  UnexpectedClientError,
} from "../models/errors/httpclienterrors.js";
import { ResponseValidationError } from "../models/errors/responsevalidationerror.js";
import { SDKValidationError } from "../models/errors/sdkvalidationerror.js";
import { ServiceError } from "../models/errors/serviceerror.js";
import {
  RemoveDashboardWidgetRequest,
  RemoveDashboardWidgetSecurity,
} from "../models/operations/removedashboardwidget.js";
import { unwrapAsync } from "../types/fp.js";
import { useGramContext } from "./_context.js";
import { MutationHookOptions } from "./_types.js";

export type RemoveDashboardWidgetMutationVariables = {
  request: RemoveDashboardWidgetRequest;
  security?: RemoveDashboardWidgetSecurity | undefined;
  options?: RequestOptions;
};

export type RemoveDashboardWidgetMutationData = Dashboard;

export type RemoveDashboardWidgetMutationError =
  | ServiceError
  | GramError
  | ResponseValidationError
  | ConnectionError
  | RequestAbortedError
  | RequestTimeoutError
  | InvalidRequestError
  | UnexpectedClientError
  | SDKValidationError;

/**
 * removeDashboardWidget dashboards
 *
 * @remarks
 * Take a card off a dashboard. The widget itself stays saved.
 */
export function useRemoveDashboardWidgetMutation(
  options?: MutationHookOptions<
    RemoveDashboardWidgetMutationData,
    RemoveDashboardWidgetMutationError,
    RemoveDashboardWidgetMutationVariables
  >,
): UseMutationResult<
  RemoveDashboardWidgetMutationData,
  RemoveDashboardWidgetMutationError,
  RemoveDashboardWidgetMutationVariables
> {
  const client = useGramContext();
  return useMutation({
    ...buildRemoveDashboardWidgetMutation(client, options),
    ...options,
  });
}

export function mutationKeyRemoveDashboardWidget(): MutationKey {
  return ["@gram/client", "dashboards", "removeWidget"];
}

export function buildRemoveDashboardWidgetMutation(
  client$: GramCore,
  hookOptions?: RequestOptions,
): {
  mutationKey: MutationKey;
  mutationFn: (
    variables: RemoveDashboardWidgetMutationVariables,
  ) => Promise<RemoveDashboardWidgetMutationData>;
} {
  return {
    mutationKey: mutationKeyRemoveDashboardWidget(),
    mutationFn: function removeDashboardWidgetMutationFn({
      request,
      security,
      options,
    }): Promise<RemoveDashboardWidgetMutationData> {
      const mergedOptions = {
        ...hookOptions,
        ...options,
        fetchOptions: {
          ...hookOptions?.fetchOptions,
          ...options?.fetchOptions,
          signal: combineSignals(
            hookOptions?.fetchOptions?.signal,
            options?.fetchOptions?.signal,
          ),
        },
      };
      return unwrapAsync(dashboardsRemoveWidget(
        client$,
        request,
        security,
        mergedOptions,
      ));
    },
  };
}
